import { useEffect, useState } from "react";
import { BottomSection, CardListContainer, EmptyContinaer } from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import {
  deleteMeetingReservation,
  getMyUpcomingMeetingReservationList,
  TReservation,
} from "apis/meetings/meeting-reservation";
import RsvDetailInfoSideModal from "./rsv-detail-info-side-modal";
import DeleteWarningModal from "./delete-modal";
import { useToast } from "hook/useToast";
import { useRecoilState, useRecoilValue } from "recoil";
import { member, user } from "recoil/recoilState";
import RsvModifySideModal from "./rsv-modify-side-modal";
import { sleep } from "utils/validation";
import { LoadingModal } from "uis/modal";
import * as Modal from "uis/modal";
import { RepeatReservationDeleteOption } from "uis/modal/repeat-reservation-delete-modal/types";
import { RepeatReservationConditionalOption } from "uis/modal/repeat-edit-conditinal-modal";
import { MyBookingListSkeleton } from "uis/MyBooking";

//* INFO: 오전 7시 - 오후 11시에 속한 예약인지 여부 확인
export const isInTimeRange = (start: string, end: string): boolean => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const startHour = startDate.getHours();
  const endHour = endDate.getHours();

  if (startHour >= 7 && endHour <= 23 && startDate.setHours(0, 0, 0, 0) === endDate.setHours(0, 0, 0, 0)) {
    return true;
  }

  return false;
};

const MyScheduledRsv = ({
  startDate = new Date(),
  endDate = new Date(),
  creatorOnly = false,
}: {
  startDate: Date;
  endDate: Date;
  creatorOnly: boolean;
}) => {
  const [userInfo, setUserInfo] = useRecoilState(user);
  const memberInfo = useRecoilValue(member);
  const { showToast } = useToast();
  const [totalPage, setTotalPage] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [reservationList, setReservationList] = useState<TReservation[]>([]);
  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openModifyModal, setOpenModifyModal] = useState<boolean>(false);
  const [selectedRsv, setSelectedRsv] = useState<TReservation>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isShowLoadingModal, setIsShowLoadingModal] = useState<boolean>(false);
  const [editOption, setEditOption] = useState<RepeatReservationConditionalOption>();
  const [openRepeatEditConditionalModal, setOpenRepeatEditConditionalModal] = useState<boolean>(false);

  //* INFO: 수정하기 버튼을 눌렀을 때 호출되는 함수
  const onEditItem = (reservation?: TReservation) => {
    if (reservation?.isRecurring) {
      setSelectedRsv(reservation);
      setOpenRepeatEditConditionalModal(true);
      return;
    }
    setSelectedRsv(reservation);
    setOpenModifyModal(true);
  };

  const onDeleteItem = (reservation?: TReservation) => {
    setSelectedRsv(reservation);
    setOpenDeleteModal(true);
  };

  const onConfirmDelete = async (option?: RepeatReservationDeleteOption) => {
    if (isShowLoadingModal) return;
    setIsShowLoadingModal(true);
    setOpenDeleteModal(false);
    if (selectedRsv?.meetingRoom?.meetingRoomIntegration?.integrationProvider === "GOOGLE") {
      await sleep(4000);
    }

    const deleteItem = {
      googleEventId: selectedRsv?.googleEventId ?? "",
      meetingReservationId: selectedRsv?.id ?? "",
      meetingRoomId: selectedRsv?.meetingRoom?.id ?? "",
      editOption: option,
    };
    const res = await deleteMeetingReservation(deleteItem);
    if (res.success) {
      setOpenDetailModal(false);
      getMyUpcomingReservation(page);
      showToast("삭제가 완료되었습니다.", "success");
    } else {
      showToast(res.message ?? "처리 중 오류가 발생했습니다. 다시 시도해주세요.", "error");
    }
    setIsShowLoadingModal(false);
  };

  const getMyUpcomingReservation = async (page: number) => {
    setLoading(true);
    const res = await getMyUpcomingMeetingReservationList(page, startDate, endDate, creatorOnly);
    if (res.data) {
      setTotalPage(res.data.totalPageNumber);
      setPage(res.data.nowPageNumber);
      setReservationList(res.data.meetingReservationList);
    }
    setLoading(false);
  };

  useEffect(() => {
    getMyUpcomingReservation(page);
  }, [startDate, endDate, creatorOnly]);

  useEffect(() => {
    if (!openDetailModal && !openDeleteModal && !openModifyModal) setSelectedRsv(undefined);
  }, [openDetailModal, openDeleteModal, openModifyModal]);

  return (
    <BottomSection>
      {loading && (
        <CardListContainer>
          <MyBookingListSkeleton />
          <MyBookingListSkeleton />
          <MyBookingListSkeleton />
          <MyBookingListSkeleton />
          <MyBookingListSkeleton />
        </CardListContainer>
      )}
      {!loading &&
        (reservationList.length === 0 ? (
          <EmptyContinaer>
            <UI.Icon name="calendar" size={24} color="var(--alias-color-label-alternative)" />
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
              예정된 예약/참석이 없습니다.
            </UI.Typography>
          </EmptyContinaer>
        ) : (
          <CardListContainer>
            {reservationList.map((reservation, index) => (
              <UI.List.ListItem.ScheduledReservation
                onClick={() => {
                  setSelectedRsv(reservation);
                  setOpenDetailModal(true);
                }}
                isSelected={selectedRsv === reservation}
                key={index}
                reservationStartDate={new Date(reservation.startTime)}
                reservationEndDate={new Date(reservation.endTime)}
                meetingRoomName={reservation.meetingRoom?.name}
                reservationTitle={reservation.title}
                creator={{
                  name: reservation.reservationCreator?.member?.nickname,
                  image: reservation.reservationCreator?.member?.image,
                  isVisitor: reservation.reservationCreator?.isVisitor,
                }}
                attendees={reservation.reservationAttendees?.map((attendee) => {
                  return {
                    name: attendee.isVisitor ? attendee.visitorEmail : attendee.member?.nickname,
                    image: attendee.member?.image,
                    isVisitor: attendee.isVisitor,
                  };
                })}
                myName={memberInfo?.nickname}
                onClickDelete={() => onDeleteItem(reservation)}
                onClickEdit={() => {
                  if (!isInTimeRange(reservation.startTime, reservation.endTime)) {
                    showToast("07:00 -23:00 이외의 시간대 예약은 수정을 지원하지 않습니다.", "warning");
                    return;
                  }
                  onEditItem(reservation);
                }}
                disabledEdit={!isInTimeRange(reservation.startTime, reservation.endTime)}
                isEditable={
                  reservation.reservationCreator?.member?.email === userInfo?.email || reservation.guestsCanModify
                }
                isDeletable={
                  reservation.reservationCreator?.member?.email === userInfo?.email || reservation.guestsCanModify
                }
              />
            ))}
          </CardListContainer>
        ))}
      {!loading && totalPage > 0 && (
        <UI.Pagination
          pageCount={totalPage}
          currentPage={page}
          onChange={(new_page) => {
            if (new_page !== page) {
              getMyUpcomingReservation(new_page);
            }
          }}
        />
      )}
      <RsvDetailInfoSideModal
        open={openDetailModal}
        setOpen={(value) => setOpenDetailModal(value)}
        canEdit={selectedRsv?.reservationCreator?.member?.email === userInfo?.email || selectedRsv?.guestsCanModify}
        onHandleDelete={onDeleteItem}
        onHandleEdit={onEditItem}
        reservation={selectedRsv}
      />
      <DeleteWarningModal
        modalIsOpen={openDeleteModal && !selectedRsv?.isRecurring}
        closeModal={() => setOpenDeleteModal(false)}
        title={`"${selectedRsv?.title ?? "-"}" 예약을 삭제하시겠습니까?`}
        content="예약을 삭제하면 복구할 수 없습니다."
        onChange={onConfirmDelete}
      />
      <Modal.RepeatReservationDeleteModal
        modalIsOpen={openDeleteModal && selectedRsv?.isRecurring === true}
        closeModal={() => setOpenDeleteModal(false)}
        onDelete={onConfirmDelete}
      />
      <Modal.RepeatEditConditionalModal
        modalIsOpen={openRepeatEditConditionalModal}
        closeModal={() => setOpenRepeatEditConditionalModal(false)}
        onHandleEdit={(option) => {
          setEditOption(option);
          setOpenRepeatEditConditionalModal(false);
          setOpenModifyModal(true);
        }}
      />
      <LoadingModal modalIsOpen={isShowLoadingModal} />
      <RsvModifySideModal
        open={openModifyModal}
        setOpen={setOpenModifyModal}
        reservation={selectedRsv}
        onUpdate={() => {
          getMyUpcomingReservation(page);
          setOpenDetailModal(false);
        }}
        editOption={editOption}
      />
    </BottomSection>
  );
};

export default MyScheduledRsv;
